import { siteAsset } from "./siteAssets";

export type SiteCastItem = {
  role: string;
  actor: string;
};

export type SiteReview = {
  text: string;
  author?: string;
};

export type SiteEvent = {
  /** Human-readable identifier used in URLs: /события/:slug */
  slug: string;
  soon: boolean;
  name: string;
  subtitle?: string;
  old?: string;
  anonse?: string;
  date?: string;
  /** Card/preview image for Events page */
  cardImage: string;
  /** Short poster used only in the mobile afisha list. */
  listImage?: string;
  /** Wide image split across the afisha side panels. */
  sideImage?: string;
  /** Half posters on the event page. Omitted or true shows them. */
  showSideOnEvent?: boolean;
  /** Optional background image for Event page */
  eventPageBg?: string;
  /** Disable frosted glass overlay on Event page */
  disableGlass?: boolean;
  type?: string;
  colorBackground?: number;
  cast?: SiteCastItem[];
  /** TicketCloud продажи билетов (опционально) */
  ticketsCloudEventId?: string;
  ticketsCloudToken?: string;
  /** Ширина контейнера описания, % от колонки страницы (40–100). */
  anonseWidth?: number;
  /** Отзывы (опционально) */
  reviews?: SiteReview[];
  /** Отзывы-картинки (опционально) */
  reviewImages?: string[];
  /**
   * Кадры галереи. Строка — путь, кадр по центру.
   * Объект — тот же путь и точка кадра в процентах (0–100).
   */
  photos?: Array<string | { src: string; focusX?: number; focusY?: number; zoom?: number }>;
  /** Амбиент (дождь): аудио и название кнопки (опционально) */
  rainAudioUrl?: string;
  rainButtonLabel?: string;
};

type SiteEventsContent = {
  version?: number;
  updatedAt?: string;
  events: SiteEvent[];
};

const EVENTS_URL = siteAsset("/content/events.json");
const CACHE_KEY = "site:events-json:v2";

function safeJsonParse<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function isValidContent(x: any): x is SiteEventsContent {
  return x && typeof x === "object" && Array.isArray(x.events);
}

/** Same data as last successful `fetchSiteEvents` (sessionStorage). Safe for first paint — avoids stale hardcoded fallbacks. */
export function readSiteEventsCache(): SiteEvent[] | null {
  if (typeof window === "undefined") return null;
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const parsed = safeJsonParse<SiteEventsContent>(cached);
    if (!parsed || !isValidContent(parsed)) return null;
    const events = parsed.events ?? [];
    return events.length ? (events as SiteEvent[]) : null;
  } catch {
    return null;
  }
}

export function clampSiteAnonseWidth(value: unknown): number {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return 100;
  const stepped = Math.round(parsed / 5) * 5;
  return Math.min(100, Math.max(40, stepped));
}

export type SiteGalleryPhoto = {
  src: string;
  focusX: number;
  focusY: number;
  zoom: number;
};

export function clampPhotoFocus(value: unknown): number {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(100, Math.max(0, Math.round(parsed)));
}

export function clampPhotoZoom(value: unknown): number {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return 1;
  const stepped = Math.round(parsed * 10) / 10;
  return Math.min(4, Math.max(1, stepped));
}

export function readGalleryPhotos(raw: unknown): SiteGalleryPhoto[] {
  if (!Array.isArray(raw)) return [];
  const photos: SiteGalleryPhoto[] = [];
  for (const item of raw) {
    if (typeof item === "string") {
      const src = item.trim();
      if (src) photos.push({ src, focusX: 50, focusY: 50, zoom: 1 });
      continue;
    }
    if (!item || typeof item !== "object" || typeof (item as { src?: unknown }).src !== "string") continue;
    const src = (item as { src: string }).src.trim();
    if (!src) continue;
    const record = item as { focusX?: unknown; focusY?: unknown; zoom?: unknown };
    photos.push({
      src,
      focusX: clampPhotoFocus(record.focusX),
      focusY: clampPhotoFocus(record.focusY),
      zoom: clampPhotoZoom(record.zoom),
    });
  }
  return photos;
}

function normalizeEvent(raw: any): SiteEvent | null {
  if (!raw || typeof raw !== "object") return null;

  const slugRaw = typeof raw.slug === "string" ? raw.slug : typeof raw.id === "string" ? raw.id : "";
  const slug = slugRaw.trim();
  const name = typeof raw.name === "string" ? raw.name : "";
  const cardImageRaw =
    typeof raw.cardImage === "string"
      ? raw.cardImage
      : typeof raw.img === "string"
        ? raw.img
        : "";
  const cardImage = cardImageRaw.trim();

  if (!slug || !name.trim() || !cardImage) return null;

  const soon = Boolean(raw.soon);
  const subtitle = typeof raw.subtitle === "string" ? raw.subtitle : undefined;
  const old = typeof raw.old === "string" ? raw.old : undefined;
  const anonse = typeof raw.anonse === "string" ? raw.anonse : undefined;
  const anonseWidthValue = clampSiteAnonseWidth(raw.anonseWidth);
  const anonseWidth = anonseWidthValue < 100 ? anonseWidthValue : undefined;
  const date = typeof raw.date === "string" ? raw.date : undefined;
  const type = typeof raw.type === "string" ? raw.type : undefined;
  const colorBackground =
    typeof raw.colorBackground === "number" ? raw.colorBackground : undefined;
  const photos = readGalleryPhotos(raw.photos).map((photo) => {
    const focusShifted = photo.focusX !== 50 || photo.focusY !== 50;
    const zoomed = photo.zoom > 1;
    if (!focusShifted && !zoomed) return photo.src;
    return {
      src: photo.src,
      ...(focusShifted ? { focusX: photo.focusX, focusY: photo.focusY } : {}),
      ...(zoomed ? { zoom: photo.zoom } : {}),
    };
  });
  const cast = Array.isArray(raw.cast)
    ? raw.cast
        .map((x: any) =>
          x && typeof x === "object" && typeof x.role === "string" && typeof x.actor === "string"
            ? { role: x.role, actor: x.actor }
            : null
        )
        .filter(Boolean)
    : undefined;
  const eventPageBg =
    typeof raw.eventPageBg === "string"
      ? raw.eventPageBg
      : typeof raw.bgImage === "string"
        ? raw.bgImage
        : undefined;
  const sideImage = typeof raw.sideImage === "string" ? raw.sideImage.trim() : undefined;
  const showSideOnEvent = raw.showSideOnEvent === false ? false : undefined;
  const listImage = typeof raw.listImage === "string" ? raw.listImage.trim() : undefined;
  const disableGlass = raw.disableGlass === true ? true : undefined;

  const ticketsCloudEventId =
    typeof raw.ticketsCloudEventId === "string" ? raw.ticketsCloudEventId.trim() : undefined;
  const ticketsCloudToken =
    typeof raw.ticketsCloudToken === "string" ? raw.ticketsCloudToken.trim() : undefined;

  const reviewsRaw = Array.isArray(raw.reviews) ? raw.reviews : null;
  const reviews = reviewsRaw
    ? (reviewsRaw
        .map((x: any) => {
          if (typeof x === "string") {
            const text = x.trim();
            return text ? ({ text } satisfies SiteReview) : null;
          }
          if (x && typeof x === "object") {
            const text = typeof x.text === "string" ? x.text.trim() : "";
            if (!text) return null;
            const author = typeof x.author === "string" ? x.author.trim() : "";
            return (author ? { text, author } : { text }) satisfies SiteReview;
          }
          return null;
        })
        .filter(Boolean) as SiteReview[])
    : undefined;

  const reviewImages =
    Array.isArray(raw.reviewImages)
      ? (raw.reviewImages
          .map((x: any) => (typeof x === "string" ? x.trim() : ""))
          .filter(Boolean) as string[])
      : undefined;

  const rainAudioUrl =
    typeof raw.rainAudioUrl === "string" ? raw.rainAudioUrl.trim() : undefined;
  const rainButtonLabel =
    typeof raw.rainButtonLabel === "string" ? raw.rainButtonLabel.trim() : undefined;

  return {
    slug,
    soon,
    name: name.trim(),
    subtitle,
    old,
    anonse,
    ...(anonseWidth ? { anonseWidth } : null),
    date,
    cardImage,
    ...(listImage ? { listImage } : null),
    ...(sideImage ? { sideImage } : null),
    ...(showSideOnEvent === false ? { showSideOnEvent: false } : null),
    ...(disableGlass ? { disableGlass: true } : null),
    type,
    colorBackground,
    photos,
    cast,
    eventPageBg,
    ...(ticketsCloudEventId ? { ticketsCloudEventId } : null),
    ...(ticketsCloudToken ? { ticketsCloudToken } : null),
    ...(reviews && reviews.length ? { reviews } : null),
    ...(reviewImages && reviewImages.length ? { reviewImages } : null),
    ...(rainAudioUrl ? { rainAudioUrl } : null),
    ...(rainButtonLabel ? { rainButtonLabel } : null),
  };
}

export async function fetchSiteEvents(opts?: {
  timeoutMs?: number;
  cache?: boolean;
}): Promise<SiteEvent[] | null> {
  const timeoutMs = Math.max(1000, Math.min(20000, Math.trunc(opts?.timeoutMs ?? 5000)));
  const cache = opts?.cache !== false;

  const cachedEvents = (() => {
    if (!cache) return null;
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const parsed = safeJsonParse<SiteEventsContent>(cached);
    return parsed && isValidContent(parsed) ? parsed.events : null;
  })();

  const ac = new AbortController();
  const t = window.setTimeout(() => ac.abort(), timeoutMs);
  try {
    const res = await fetch(EVENTS_URL, {
      cache: "no-store",
      signal: ac.signal,
    });
    if (!res.ok) return cachedEvents;
    const text = await res.text();
    const parsed = safeJsonParse<SiteEventsContent>(text);
    if (!parsed || !isValidContent(parsed)) return cachedEvents;
    const normalized = (parsed.events ?? []).map(normalizeEvent).filter(Boolean) as SiteEvent[];
    const cachedValue: SiteEventsContent = {
      version: parsed.version,
      updatedAt: parsed.updatedAt,
      events: normalized,
    };
    if (cache) sessionStorage.setItem(CACHE_KEY, JSON.stringify(cachedValue));
    return normalized;
  } catch {
    return cachedEvents;
  } finally {
    window.clearTimeout(t);
  }
}

function pickText(primary?: string, fallback?: string) {
  const trimmed = primary?.trim();
  return trimmed ? primary : fallback;
}

/** Дополняет remote-афишу fallback-полями, если в JSON пустые строки. */
export function mergeSiteEventsWithFallback(
  remote: SiteEvent[],
  fallback: SiteEvent[]
): SiteEvent[] {
  const fallbackBySlug = new Map(fallback.map((event) => [event.slug, event]));

  return remote.map((event) => {
    const fb = fallbackBySlug.get(event.slug);
    if (!fb) return event;

    return {
      ...fb,
      ...event,
      subtitle: event.subtitle?.trim() ? event.subtitle : undefined,
      old: pickText(event.old, fb.old),
      anonse: pickText(event.anonse, fb.anonse),
      anonseWidth: event.anonseWidth ?? fb.anonseWidth,
      date: pickText(event.date, fb.date),
      type: pickText(event.type, fb.type),
      eventPageBg: pickText(event.eventPageBg, fb.eventPageBg),
      sideImage: pickText(event.sideImage, fb.sideImage),
      showSideOnEvent: event.showSideOnEvent === false ? false : fb.showSideOnEvent,
      listImage: pickText(event.listImage, fb.listImage),
      rainAudioUrl: pickText(event.rainAudioUrl, fb.rainAudioUrl),
      rainButtonLabel: pickText(event.rainButtonLabel, fb.rainButtonLabel),
      ticketsCloudEventId: pickText(event.ticketsCloudEventId, fb.ticketsCloudEventId),
      ticketsCloudToken: pickText(event.ticketsCloudToken, fb.ticketsCloudToken),
      colorBackground: event.colorBackground ?? fb.colorBackground,
      disableGlass: event.disableGlass ?? fb.disableGlass,
      cast: event.cast?.length ? event.cast : fb.cast,
      photos: event.photos?.length ? event.photos : fb.photos,
      reviews: event.reviews?.length ? event.reviews : fb.reviews,
      reviewImages: event.reviewImages?.length ? event.reviewImages : fb.reviewImages,
    };
  });
}

